import React from 'react';
import Icon from '../../../components/AppIcon';

const PhilosophySection = () => {
  const philosophyPrinciples = [
    {
      icon: "Target",
      title: "Business Impact First",
      description: "Every line of code should serve a business purpose. I focus on solutions that drive revenue, reduce costs, or improve user experience—not just technical elegance for its own sake."
    },
    {
      icon: "Shield",
      title: "Reliability Over Novelty",
      description: "Proven technologies and battle-tested patterns trump the latest trends. Your users don't care about your tech stack—they care that your product works flawlessly every time."
    },
    {
      icon: "Zap",
      title: "Speed Meets Quality",
      description: "Fast delivery doesn't mean cutting corners. Through efficient workflows and smart architecture decisions, I deliver production-ready solutions quickly without sacrificing quality."
    },
    {
      icon: "Users",
      title: "Clear Communication",
      description: "Technical jargon has its place—but not in client conversations. I translate complex technical concepts into business language that stakeholders can understand and act upon."
    },
    {
      icon: "TrendingUp",
      title: "Scalability by Design",
      description: "Today's solution should support tomorrow's growth. I architect systems that scale gracefully, avoiding the costly rewrites that plague many fast-growing companies."
    },
    {
      icon: "Heart",
      title: "Long-term Partnership",
      description: "I'm not just building a project—I'm building a relationship. My success is measured by your continued success, not just project completion."
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Icon name="Lightbulb" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">Core Values</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4 md:mb-6">
            My Development Philosophy
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            These principles guide every project I take on, every decision I make, and every line of code I write. They're not just words—they're commitments.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {philosophyPrinciples?.map((principle, index) => (
            <div
              key={index}
              className="group bg-card rounded-xl p-6 md:p-8 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-full flex flex-col min-h-0"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                  <Icon name={principle?.icon} size={24} className="text-primary md:w-7 md:h-7" />
                </div>
                <h3 className="text-lg md:text-xl font-bold font-heading text-foreground break-words w-full">
                  {principle?.title}
                </h3>
              </div>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed break-words w-full flex-1 overflow-auto">
                {principle?.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 md:p-12 border border-primary/20">
          <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-8">
            <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary flex items-center justify-center">
              <Icon name="Quote" size={32} className="text-primary-foreground md:w-10 md:h-10" />
            </div>
            <div className="flex-1 text-center lg:text-left">
              <p className="text-lg md:text-xl lg:text-2xl font-medium text-foreground mb-2">
                "I don't just love to code—I love to solve problems that matter."
              </p>
              <p className="text-sm md:text-base text-muted-foreground">
                This isn't about technical perfection for its own sake. It's about creating solutions that make a real difference in people's lives and businesses' bottom lines.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;